import React from 'react';
import { AlertTriangle, Home, Building2, Wind, Lightbulb, ArrowRight, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
export function ServicePathways() {
  const pathways = [{
    id: 'residential',
    title: 'Residential',
    description: 'From new builds to renovations — lighting, power, switchboards, and fault finding.',
    icon: Home,
    number: '01'
  }, {
    id: 'commercial',
    title: 'Commercial',
    description: 'Lighting, power, and fit-outs for builders, mechanics, sheds, and commercial premises.',
    icon: Building2,
    number: '02'
  }, {
    id: 'aircon',
    title: 'Air Conditioning',
    description: 'Split system supply, installation, and replacement to keep you comfortable year-round.',
    icon: Wind,
    number: '03'
  }, {
    id: 'newbuild',
    title: 'New Build Lighting',
    description: 'Upgrade your builder-standard lighting to premium pendants, LEDs, and feature lighting.',
    icon: Lightbulb,
    number: '04'
  }];
  return <section className="py-24 lg:py-32 bg-white" id="services" data-id="element-517">
      <div className="max-w-7xl mx-auto px-4" data-id="element-518">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-519">
          <span className="text-orange font-bold tracking-wider uppercase text-sm mb-3 block" data-id="element-520">
            Service Pathways
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-navy mb-6 tracking-tighter" data-id="element-521">
            How Can We Help You Today?
          </h2>
          <p className="text-lg text-slate leading-relaxed" data-id="element-522">
            Select a service pathway below to find the right solution for your
            home or business.
          </p>
        </div>

        <div className="space-y-6" data-id="element-523">
          {/* Emergency Card - Full Width */}
          <motion.a href="tel:0418381988" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="group flex flex-col md:flex-row items-center justify-between bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 relative overflow-hidden animate-pulse-glow" data-id="element-524">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiNmZmZmZmYiLz48L3N2Zz4=')]" data-id="element-525"></div>

            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 relative z-10 text-center md:text-left w-full" data-id="element-526">
              <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm relative" data-id="element-527">
                <div className="absolute inset-0 border-2 border-white/50 rounded-2xl animate-ping opacity-75" data-id="element-528"></div>
                <AlertTriangle className="w-10 h-10 text-white" data-id="element-529" />
              </div>
              <div className="flex-1" data-id="element-530">
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2" data-id="element-531">
                  <h3 className="text-2xl md:text-3xl font-heading font-extrabold text-white" data-id="element-532">
                    24/7 Emergency Electrical
                  </h3>
                  <span className="inline-flex items-center justify-center bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full border border-white/30 backdrop-blur-sm w-max mx-auto md:mx-0" data-id="element-533">
                    Fast Response
                  </span>
                </div>
                <p className="text-red-100 text-lg max-w-2xl" data-id="element-534">
                  Fast response for urgent electrical faults, power outages, and
                  safety hazards across Perth's south.
                </p>
              </div>
              <div className="mt-6 md:mt-0 flex-shrink-0" data-id="element-535">
                <div className="bg-white text-red-600 px-8 py-4 rounded-xl font-extrabold text-lg flex items-center gap-3 shadow-lg group-hover:bg-gray-50 transition-colors" data-id="element-536">
                  <Phone className="w-5 h-5 animate-pulse" data-id="element-537" />
                  Call Now: 0418 381 988
                </div>
              </div>
            </div>
          </motion.a>

          {/* Standard Service Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-538">
            {pathways.map((pathway, index) => <motion.a href={`#${pathway.id}`} key={pathway.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="group flex flex-col h-full bg-white rounded-2xl border border-gray-100 p-6 lg:p-7 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden" data-id="element-539">
                {/* Colored Top Border */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-orange transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-540"></div>

                {/* Background Number */}
                <div className="absolute -bottom-4 -right-4 text-8xl font-heading font-extrabold text-gray-50 opacity-50 group-hover:text-orange/5 transition-colors pointer-events-none select-none" data-id="element-541">
                  {pathway.number}
                </div>

                <div className="w-14 h-14 bg-cream rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange/10 transition-colors relative z-10" data-id="element-542">
                  <pathway.icon className="w-7 h-7 text-navy group-hover:text-orange transition-colors" data-id="element-543" />
                </div>

                <h3 className="text-xl font-heading font-extrabold text-navy mb-3 relative z-10" data-id="element-544">
                  {pathway.title}
                </h3>
                <p className="text-slate text-sm flex-grow mb-6 relative z-10 leading-relaxed" data-id="element-545">
                  {pathway.description}
                </p>

                <div className="flex items-center justify-between mt-auto relative z-10" data-id="element-546">
                  <div className="flex items-center gap-2 text-sm font-extrabold text-navy group-hover:text-orange transition-colors" data-id="element-547">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-548" />
                  </div>
                  <span className="bg-orange/10 text-orange text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-549">
                    Free Quote
                  </span>
                </div>
              </motion.a>)}
          </div>
        </div>
      </div>
    </section>;
}